import { Box } from '@chakra-ui/react';
import React from 'react';
import { AmplifyCore } from '@codametrix/ui-common';
import { styleOverrides, styles } from './service-desk.styles';
import { getServiceDeskClient } from '../../clients/service-desk.client';
import { displayJsonInNewWindow } from '../../core/utils/json-view';
import type { RemoteVerticalNavReport } from '../../actions/vertical-nav.action';

const { conditionalUtils } = AmplifyCore;
const classes = styles();

/** The host-owned props the SDK client is wired from; the remote invokes the callbacks. */
type ServiceDeskViewProps = AppProps.ServiceDeskView & {
  coderViewV2?: boolean;
  canPrioritize?: boolean;
  onOpenPrioritizeWork?: () => void;
  onVerticalNavResolved?: (report: RemoteVerticalNavReport) => void;
};

class ServiceDesk extends React.Component<ServiceDeskViewProps, {}> {
  static defaultProps = {
    viewType: 'GRID_WIDGET_WORKLIST_SETTINGS'
  };

  /**
   * The SDK is handed this object once at create time and keeps the same reference. Writing a new
   * value into it schedules no render in the remote's own React root, so the remote picks it up
   * only on its next render: this keeps the value from being stale, it does not make it live. The
   * modal's own resource check is the boundary.
   */
  private widgetOptions: Record<string, any> | null = null;

  /**
   * The SDK never unmounts a previous root, so a detached worklist keeps running and can still
   * invoke the callbacks below. Reports carry the mount they came from so the reducer can drop a
   * superseded one.
   */
  private static mountGeneration = 0;

  private generation = 0;

  componentDidUpdate(prevProps: ServiceDeskViewProps): void {
    const canPrioritize = !!this.props.canPrioritize;
    if (this.widgetOptions && canPrioritize !== !!prevProps.canPrioritize) {
      this.widgetOptions.canPrioritize = canPrioritize;
    }
  }

  componentDidMount(): void {
    ServiceDesk.mountGeneration += 1;
    this.generation = ServiceDesk.mountGeneration;

    const onWorkItemSelectHandler = (workitem: any, worklistContext: any) => {
      this.props.openAmplifyCase({
        serviceLine: workitem.service,
        caseId: workitem.cmx_case_uid,
        processId: workitem.cmx_process_id,
        workitemId: workitem.case_id,
        servicedeskMode: true,
        worklistContext,
        isCodingQuality: worklistContext?.isCodingQuality,
        isQualityAssessment: worklistContext?.isQualityAssessment,
        hasAmplifyCaseRunV2Access: this.props.hasAmplifyCaseRunV2Access,
        qaCollectionUuid: workitem.qa_collection_uuid
      });
    };

    const viewWorkitemDataHandler = (workitem: any) => {
      displayJsonInNewWindow(workitem);
    };

    const { capabilities } = this.props;
    // CDMX-15947 — in worklist V2 the SDK renders a header-slot placeholder in
    // place of the ProductivityTracker; dashboard-ui portals the coder "My Work"
    // top section into it. V1 / non-V2 keep the ProductivityTracker.
    const coderViewV2 = this.props.coderViewV2;

    // The opener reads this.props on every call rather than closing over the mount-time value.
    const canPrioritize = !!this.props.canPrioritize;

    const widgetOptions = {
      onWorkItemSelected: onWorkItemSelectHandler,
      coderViewV2,
      prodTrackerActive: conditionalUtils.capabilityValue(
        capabilities?.capabilities.productivityTracker?.on
      ),
      childRef: this.props?.childRef,
      userContext: this.props?.userContext,
      viewWorkitemData: viewWorkitemDataHandler,
      canPrioritize,
      onOpenPrioritizeWork: () => this.props.onOpenPrioritizeWork?.(),
      // How the remote reports its own `vertical-view-selector` answer — see
      // selectHidePrioritizeNav. Reads this.props on every call, not the mount-time handler.
      onVerticalNavResolved: (report: RemoteVerticalNavReport) =>
        this.props.onVerticalNavResolved?.({
          ...report,
          generation: this.generation
        })
    };

    this.widgetOptions = widgetOptions;

    const xcaliberClient = getServiceDeskClient();
    xcaliberClient.create(this.props.viewType, 'service_desk', widgetOptions);
  }
  render(): React.ReactNode {
    return (
      <div style={classes.root}>
        {/* @ts-ignore */}
        <Box css={styleOverrides} id="service_desk"></Box>
      </div>
    );
  }
}

export { ServiceDesk };
